import React, { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'

import { ordersListPath } from 'routes/url-constants'
import { selectBellNotifications } from 'store/notifications/notifications.selectors'

import { ORDER_STATUS_CHANGED_EVENT } from './CustomerOrderStatusMonitor'

const BASE_TITLE = 'ОТК'
const TAB_ALERT_CLEAR_EVENT = 'otk-tab-alert-clear'

function normalizePath(pathname: string) {
  if (!pathname) return ''
  if (pathname === '/') return '/'
  return pathname.endsWith('/') ? pathname.slice(0, -1) : pathname
}

/**
 * Мигание и префиксы в `document.title`: чат (socket) и смена статуса заявки (CUSTOMER).
 */
export const AppTabTitle: React.FC = () => {
  const notifications = useSelector(selectBellNotifications)
  const notificationCount = notifications?.length ?? 0
  const location = useLocation()
  const [orderStatusTabAlert, setOrderStatusTabAlert] = useState(false)
  const [chatTabAlert, setChatTabAlert] = useState(false)
  const notificationCountRef = useRef(notificationCount)
  const lastSeenNotificationCountRef = useRef(notificationCount)

  useEffect(() => {
    notificationCountRef.current = notificationCount
  }, [notificationCount])

  useEffect(() => {
    const onOrderStatus = () => setOrderStatusTabAlert(true)
    window.addEventListener(ORDER_STATUS_CHANGED_EVENT, onOrderStatus)
    return () => window.removeEventListener(ORDER_STATUS_CHANGED_EVENT, onOrderStatus)
  }, [])

  useEffect(() => {
    // Включаем чат-алерт только когда приходят новые нотификации после "последнего просмотра".
    if (notificationCount > lastSeenNotificationCountRef.current) {
      setChatTabAlert(true)
    }
  }, [notificationCount])

  useEffect(() => {
    const clearLocalAlerts = () => {
      setOrderStatusTabAlert(false)
      setChatTabAlert(false)
      lastSeenNotificationCountRef.current = notificationCountRef.current
    }

    const broadcastClear = () => {
      // Сообщаем всем вкладкам, что пользователь "посмотрел" — сбрасываем title-алерты везде.
      try {
        if ('BroadcastChannel' in window) {
          const bc = new BroadcastChannel(TAB_ALERT_CLEAR_EVENT)
          bc.postMessage({ ts: Date.now() })
          bc.close()
          return
        }
      } catch {
        // ignore
      }

      try {
        localStorage.setItem(TAB_ALERT_CLEAR_EVENT, String(Date.now()))
      } catch {
        // ignore
      }
    }

    const onAnyTabClear = () => clearLocalAlerts()

    let bc: BroadcastChannel | null = null
    try {
      if ('BroadcastChannel' in window) {
        bc = new BroadcastChannel(TAB_ALERT_CLEAR_EVENT)
        bc.onmessage = onAnyTabClear
      }
    } catch {
      bc = null
    }

    const onStorage = (e: StorageEvent) => {
      if (e.key === TAB_ALERT_CLEAR_EVENT) onAnyTabClear()
    }

    const onFocusOrVisible = () => {
      if (document.visibilityState === 'visible') {
        broadcastClear()
        clearLocalAlerts()
      }
    }

    window.addEventListener('focus', onFocusOrVisible)
    document.addEventListener('visibilitychange', onFocusOrVisible)
    window.addEventListener('storage', onStorage)

    return () => {
      window.removeEventListener('focus', onFocusOrVisible)
      document.removeEventListener('visibilitychange', onFocusOrVisible)
      window.removeEventListener('storage', onStorage)
      if (bc) bc.close()
    }
  }, [])

  useEffect(() => {
    if (normalizePath(location.pathname) === ordersListPath) {
      setOrderStatusTabAlert(false)
    }
  }, [location.pathname])

  useEffect(() => {
    const count = notificationCount

    const isOnOrdersListPage = normalizePath(location.pathname) === ordersListPath
    const isHidden = document.visibilityState !== 'visible'

    // 1) Чат-уведомления (socket)
    if (chatTabAlert && count > 0 && !isOnOrdersListPage) {
      if (!isHidden) {
        document.title = `(${count}) ${BASE_TITLE}`
        return
      }

      let toggle = false
      const intervalId = window.setInterval(() => {
        toggle = !toggle
        document.title = toggle ? `(${count}) ${BASE_TITLE}` : BASE_TITLE
      }, 900)

      return () => {
        window.clearInterval(intervalId)
        document.title = BASE_TITLE
      }
    }

    // 2) Смена статуса заявки (CUSTOMER, глобальный монитор)
    if (orderStatusTabAlert) {
      if (!isHidden) {
        document.title = `(!) ${BASE_TITLE}`
        return
      }

      let toggle = false
      const intervalId = window.setInterval(() => {
        toggle = !toggle
        document.title = toggle ? `(!) ${BASE_TITLE}` : `Статус заявки · ${BASE_TITLE}`
      }, 900)

      return () => {
        window.clearInterval(intervalId)
        document.title = BASE_TITLE
      }
    }

    document.title = BASE_TITLE
  }, [chatTabAlert, notificationCount, location.pathname, orderStatusTabAlert])

  return null
}
